import {
  isMongoId,
  withSortableParams,
  optionalCountryCodeValidator,
  withPaginationParamsOptional,
} from '../../helpers';
import { z } from 'zod';
import { AssetType } from 'further-types/lp/asset';
import { CurrencyCode } from 'further-types/lp/vehicle';
import { CalendarDay } from '../../../utils';

export const List = withSortableParams(
  z.object({
    assetType: z.nativeEnum(AssetType),
    vehicleId: z.string().refine(isMongoId(), {
      message: 'Invalid vehicle ID',
    }),
  }),
  ['assetType'],
);

export const Get = z.object({
  assetId: z.string().refine(isMongoId(), {
    message: 'Invalid asset ID',
  }),
});

export const GetInvestmentHistory = withPaginationParamsOptional(
  z.object({
    assetId: z.string().refine(isMongoId(), {
      message: 'Invalid asset ID',
    }),
    vehicleId: z.string().refine(isMongoId(), {
      message: 'Invalid vehicle ID',
    }),
    date: z.instanceof(CalendarDay).optional(),
  }),
  ['dealCompletionDate'],
);

export const GetAssetPerformanceAggregations = z.object({
  assetId: z.string().refine(isMongoId(), {
    message: 'Invalid asset ID',
  }),
});

export const GetAssetHoldingClassInvestmentTotals = z.object({
  assetId: z.string().refine(isMongoId(), {
    message: 'Invalid asset ID',
  }),
  date: z.instanceof(CalendarDay).optional(),
});

export const GetAssetRevaluations = z.object({
  assetId: z.string().refine(isMongoId(), {
    message: 'Invalid asset ID',
  }),
});

export const GetAssetFullHistory = z.object({
  assetId: z.string().refine(isMongoId(), {
    message: 'Invalid asset ID',
  }),
});

export const NewAssetSchema = z.object({
  isNewAsset: z.literal(true),
  asset: z.object({
    assetType: z.nativeEnum(AssetType),
    name: z.string({
      required_error: 'Trading name is required',
    }),
    fullLegalName: z.string({
      required_error: 'Full legal name is required',
    }),
    baseCurrency: z.nativeEnum(CurrencyCode, {
      errorMap: () => ({ message: 'Base currency is required' }),
    }),
    registeredNumber: z.string().optional(),
    registrationCountry: z
      .string()
      .optional()
      .superRefine((val, ctx) => {
        if (!optionalCountryCodeValidator(val)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Please provide a valid registration country code',
          });
        }
      }),
    registeredAddressLine1: z.string().optional(),
    registeredAddressLine2: z.string().optional(),
    registeredAddressTownCity: z.string().optional(),
    registeredAddressPostalCode: z.string().optional(),
    registeredAddressCountry: z
      .string()
      .optional()
      .superRefine((val, ctx) => {
        if (!optionalCountryCodeValidator(val)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Please provide a valid address country code',
          });
        }
      }),
    // Portal information fields
    description: z.string().optional(),
    companyLogo: z
      .object({
        path: z.string(),
        name: z.string(),
        preview: z.string(),
      })
      .optional()
      .nullable(),
    websiteUrl: z.string().optional(),
    twitterUrl: z.string().optional(),
    linkedinUrl: z.string().optional(),
    stage: z.string().optional(),
    sector: z.string().optional(),
  }),
});

export const ExistingAssetSchema = z.object({
  isNewAsset: z.literal(false),
  tradingName: z.string().optional(),
  assetId: z.string().refine(isMongoId(), {
    message: 'Please select an existing asset or create a new one',
  }),
});
